import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X, Check, Search } from "lucide-react";

/**
 * MultiSelectPills — searchable multi-select with grouped categories.
 *
 * Behaviour:
 *  - Chip / pill UI shows current selections; click the trigger to open the popup.
 *  - When open, a search input at the top of the popup auto-focuses. Typing
 *    filters BOTH item names AND category names, case-insensitive.
 *  - Categories with zero matching items collapse; a full-empty search shows
 *    a "no matches" state.
 *  - The `options` prop is already filtered upstream for same-day conflicts
 *    (see `ShootCrew.jsx` → `availableEquipment`), so hidden items never
 *    enter the search corpus.
 *
 * Props:
 *  - value:     string[] currently selected
 *  - onChange:  (next: string[]) => void
 *  - options:   [{ category: string, items: string[] }, ...]
 *  - disabled?: boolean
 *  - placeholder?: string
 *  - testid?:   string  (used as `data-testid` prefix)
 */
export default function MultiSelectPills({
  value = [],
  onChange,
  options = [],
  disabled = false,
  placeholder = "Select…",
  testid = "multi-select",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Reset search + focus input when opening.
  useEffect(() => {
    if (open) {
      setQuery("");
      // Delay one frame so the input is in the DOM before focusing.
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  const toggle = (item) => {
    if (disabled) return;
    onChange(value.includes(item) ? value.filter((v) => v !== item) : [...value, item]);
  };
  const remove = (item, e) => {
    e.stopPropagation();
    toggle(item);
  };

  // Case-insensitive filter: a group is included if its category matches
  // OR any of its items match. Only matching items within a partial-match
  // group are shown (unless the category itself matched — then all items stay).
  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options
      .map((group) => {
        const catMatch = group.category.toLowerCase().includes(q);
        const items = catMatch ? group.items : group.items.filter((it) => it.toLowerCase().includes(q));
        return items.length ? { category: group.category, items } : null;
      })
      .filter(Boolean);
  }, [options, query]);

  const totalMatches = useMemo(
    () => filteredOptions.reduce((sum, g) => sum + g.items.length, 0),
    [filteredOptions],
  );

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); }
          if (e.key === "Escape") setOpen(false);
        }}
        className={`w-full min-h-[2.25rem] px-2 py-1 border rounded-md text-left text-xs flex items-center gap-1 flex-wrap bg-white transition-colors cursor-pointer ${
          open ? "border-rose-400 ring-1 ring-rose-200" : "border-stone-300 hover:border-stone-400"
        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        data-testid={`${testid}-trigger`}
      >
        {value.length === 0 && (
          <span className="text-stone-400">{placeholder}</span>
        )}
        {value.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 bg-rose-50 border border-rose-200 text-rose-900 rounded-full pl-2 pr-0.5 py-0.5 text-[10px] font-semibold whitespace-nowrap"
            data-testid={`${testid}-pill-${v}`}
          >
            {v}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => remove(v, e)}
                className="p-0.5 rounded-full hover:bg-rose-100"
                aria-label={`Remove ${v}`}
              >
                <X className="w-2.5 h-2.5" strokeWidth={2.4} />
              </button>
            )}
          </span>
        ))}
        <ChevronDown className={`w-3 h-3 ml-auto shrink-0 text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} />
      </div>

      {open && (
        <div
          className="absolute z-30 top-full left-0 mt-1 w-72 bg-white border border-stone-200 rounded-lg shadow-xl overflow-hidden flex flex-col"
          data-testid={`${testid}-menu`}
          // Prevent the outside-click handler from firing on our own popup.
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Search bar */}
          <div className="p-2 border-b border-stone-100 bg-stone-50">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={2} />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") { e.stopPropagation(); setOpen(false); }
                }}
                placeholder="Type to search… (e.g. a7, Sigma)"
                className="w-full pl-7 pr-6 py-1.5 text-xs border border-stone-200 rounded bg-white focus:outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-200"
                data-testid={`${testid}-search`}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(""); searchRef.current?.focus(); }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-stone-200 text-stone-500"
                  aria-label="Clear search"
                  data-testid={`${testid}-search-clear`}
                >
                  <X className="w-3 h-3" strokeWidth={2.4} />
                </button>
              )}
            </div>
          </div>

          {/* Grouped options */}
          <div className="max-h-64 overflow-y-auto p-2 space-y-3">
            {filteredOptions.map((group) => (
              <div key={group.category}>
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold px-1 pb-1">
                  {group.category}
                </div>
                <div className="grid grid-cols-1 gap-0.5">
                  {group.items.map((item) => {
                    const on = value.includes(item);
                    return (
                      <button
                        type="button"
                        key={item}
                        onClick={() => toggle(item)}
                        className={`text-xs flex items-center gap-2 px-2 py-1 rounded hover:bg-stone-50 text-left ${
                          on ? "text-rose-800 font-semibold" : "text-stone-700"
                        }`}
                        data-testid={`${testid}-option-${item}`}
                      >
                        <span
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                            on ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-stone-300"
                          }`}
                        >
                          {on && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                        </span>
                        <HighlightedText text={item} query={query} />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {options.length === 0 && (
              <div className="text-xs text-stone-400 px-2 py-3 text-center" data-testid={`${testid}-empty`}>
                No options available
              </div>
            )}
            {options.length > 0 && totalMatches === 0 && (
              <div className="text-xs text-stone-400 px-2 py-3 text-center" data-testid={`${testid}-no-matches`}>
                No matches for &ldquo;{query}&rdquo;
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Renders text with the matched substring wrapped in a subtle highlight
 * span. Falls back to raw text when no query.
 */
function HighlightedText({ text, query }) {
  if (!query) return <>{text}</>;
  const q = query.trim();
  if (!q) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-amber-100 text-amber-900 rounded-sm px-0.5 py-0">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}
